import { forwardRef } from 'react'
import { ChevronDown } from 'lucide-react'

interface SelectOption {
  value: string | number
  label: string
  disabled?: boolean
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  error?: string
  helper?: string
  options?: SelectOption[]
  placeholder?: string
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, helper, options = [], placeholder = 'Bitte auswählen...', className = '', required = false, ...props }, ref) => {
    // Footgun-Schutz: ein controlled <select> mit leerem Wert, aber OHNE placeholder
    // und OHNE leere Option, zeigt sonst fälschlich die ERSTE Option an (Phantomwert,
    // siehe Bug #74 Stammblatt). Wir rendern dann eine leere Option, damit der echte
    // (leere) Zustand sichtbar bleibt. Greift NUR exakt in diesem Fall.
    const hasEmptyOption = options.some(o => String(o.value) === '')
    const needsEmptyGuard = props.value === '' && !placeholder && !hasEmptyOption
    return (
      <div className={className}>
        {label && <label className="label">{label}{required && <span className="ml-1 text-red-500">*</span>}</label>}
        <div className="relative">
          <select ref={ref} className={`select appearance-none pr-10 ${error ? 'input-error' : ''}`} {...props}>
            {placeholder && <option value="">{placeholder}</option>}
            {needsEmptyGuard && <option value="">—</option>}
            {options.map(o => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        </div>
        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        {helper && !error && <p className="mt-1 text-sm text-gray-500">{helper}</p>}
      </div>
    )
  }
)
Select.displayName = 'Select'
export default Select
